import { FootPrintPadHoleType, PcbNodeTypes, PcbViaType, } from "@buildwithflux/models";
import { SubscriptionManager } from "@buildwithflux/repositories";
import { Records } from "@buildwithflux/shared";
import { UndirectedGraph } from "graphology";
import { connectedComponents } from "graphology-components";
import RBush from "rbush";
import { Euler, Vector2, Vector3 } from "three";
import { vec2, ZeroEuler, ZeroVector3 } from "../common";
import { filterTruthy } from "../helpers";
import { traceGraphTolerance } from "../pcb/layout_engine/constants";
// TODO: migrate over
function vector2FromXY(xy) {
    return new Vector2(xy.x, xy.y);
}
function vector3FromXYZ(xyz) {
    return new Vector3(xyz.x, xyz.y, xyz.z);
}
function eulerFromXYZ(xyz, order = "XYZ") {
    return new Euler(xyz.x, xyz.y, xyz.z, order);
}
// Internal helpers
const areNodesConnected = (node1, node2) => {
    return vec2.distance(node1.documentPos, node2.documentPos) <= traceGraphTolerance;
};
const areNodesInSameLayer = (node1, node2) => {
    return (node1.connectedLayers === "All" ||
        node2.connectedLayers === "All" ||
        node1.connectedLayers.some((lA) => node2.connectedLayers.includes(lA)));
};
// TODO: rename to connected copper graph. This differs from the existing connectivity graph, which describes electrical/net connectivity. Consider renaming that too
export class PcbTracingGraph {
    graph;
    connectedComponents = [];
    netToConnectedComponents = {};
    /** For now we support only global subscriptions, but if needed we can support granular subscriptions in the future */
    subscriptionManager = new SubscriptionManager();
    /** To solve race conditions with the baked document data being more updated than this store */
    isDataUpdated = false;
    constructor() {
        // We build a graph with
        // - Pads, vias and trace endpoints as nodes
        // - Traces and found connections as edges
        this.graph = new UndirectedGraph();
    }
    static normalizeUid(uid) {
        return uid.replace(/_start$/, "").replace(/_end$/, "");
    }
    updateGraph(inputs) {
        const result = {
            neighborsOfChangedNodes: [],
        };
        const { bakedNodesChanges } = inputs;
        this.isDataUpdated = true;
        // Only node changes or net changes need to update the graph
        if (!bakedNodesChanges) {
            return result;
        }
        const nodesToSet = Array.from(new Set([
            ...(bakedNodesChanges?.set ?? []),
            ...(bakedNodesChanges?.add ?? []),
            ...(bakedNodesChanges?.replace ?? []),
        ]));
        const inactiveNodes = nodesToSet.filter((nodeUid) => {
            return !inputs.pcbLayoutNodes[nodeUid]?.bakedRules?.active;
        });
        // NOTE: If we see that we are trying to reset all baked nodes, that indicates
        // a full rebake of the entire board. In this case, it is faster to rebuild the
        // graph from scratch because in `updateConnectivityByDistance` we have logic
        // to check if any "foundEdge" are invalid, which is expensive; and in this case,
        // it is a waste of time to check that because we need to rebuild the entire
        // graph anyways.
        if (nodesToSet.length === Records.count(inputs.pcbLayoutNodes)) {
            this.graph.clear();
        }
        const neighborsOfChangedNodesSet = new Set();
        // Handle remove node operation
        if (bakedNodesChanges?.remove && bakedNodesChanges?.remove.length > 0) {
            this.removeNodes(bakedNodesChanges.remove, (uid) => neighborsOfChangedNodesSet.add(uid));
        }
        // Query nodes connected to route segment uids via pcb tracing graph - if nodes don't exist for
        // graph node keys with _start/_end suffix, then the node if not a route segment, or is not connected
        // to any other route segment
        const collectRouteNeighborsOfNods = (routeUids) => {
            if (!routeUids)
                return;
            for (const uid of routeUids) {
                const start = `${uid}_start`;
                const end = `${uid}_end`;
                if (this.graph.hasNode(start)) {
                    for (const neighbor of this.graph.neighbors(start)) {
                        neighborsOfChangedNodesSet.add(neighbor);
                    }
                }
                if (this.graph.hasNode(end)) {
                    for (const neighbor of this.graph.neighbors(end)) {
                        neighborsOfChangedNodesSet.add(neighbor);
                    }
                }
            }
        };
        // Handle update node operations
        collectRouteNeighborsOfNods(bakedNodesChanges?.replace);
        collectRouteNeighborsOfNods(bakedNodesChanges?.set);
        // Handle inactive nodes (i.e. nodes whose "Enabled" rule is false)
        this.removeNodes(inactiveNodes, (uid) => neighborsOfChangedNodesSet.add(uid));
        // Handle add/set node operation, we only care about active nodes
        const activeNodesToSet = nodesToSet.filter((nodeUid) => inputs.pcbLayoutNodes[nodeUid]?.bakedRules?.active);
        const { newNodes, newEdges } = this.generateGraphObjects(inputs, activeNodesToSet);
        // Incrementally set new graph nodes/edges in graph
        this.setGraphObjects(newNodes, newEdges);
        // Construct the RTree by loading all existing + new graph nodes to it
        const tree = this.constructRTree();
        this.updateConnectivityByDistance(newNodes, tree);
        // =================================================================
        // CONNECTED COMPONENTS
        // TODO: do this incrementally
        this.connectedComponents = connectedComponents(this.graph);
        const netMapArray = Object.entries(inputs.netMap);
        // The connected components associated to each net, used later to compute airwires
        this.netToConnectedComponents = Object.fromEntries(netMapArray.map(([netId, terminalIdsObj]) => {
            // HACK: it's possible that for some reason the net map contains uids that are not pcbLayoutNodes
            // For example, sometimes for the GND net the uid of the gnd schematic object is present
            const terminalIds = Object.keys(terminalIdsObj).filter((id) => inputs.pcbLayoutNodes[id]);
            // For every net, every terminal should be part of the same connected component
            const connectedComponentsRelatedToNet = Array.from(new Set(filterTruthy(terminalIds.map((terminalId) => this.connectedComponents.find((cc) => cc.includes(terminalId))))));
            return [netId, connectedComponentsRelatedToNet];
        }));
        // In this final step, we're cleaning up the neighbor list. First, filter by uids that are still in the graph since
        // it's possible that a node added to this set was itself later removed from the graph. Second, clean up any _end/_start
        // suffix on uid's, which is a special uid case for route segment nodes to distinguish start/end vertices of a segment
        const neighborsSet = new Set();
        for (const uid of neighborsOfChangedNodesSet) {
            if (this.graph.hasNode(uid)) {
                neighborsSet.add(uid.replace(/(_end|_start)$/gi, ""));
            }
        }
        result.neighborsOfChangedNodes = Array.from(neighborsSet);
        // =================================================================
        // SUBSCRIPTIONS
        this.subscriptionManager.notify("update", this);
        return result;
    }
    /**
     * Returns the latest graph.
     *
     * TODO: This is not good tho because this means one could accidentally mutate the graph via
     * this method, without going through this class... However, I dont think of a good way to
     * improve this yet. One thought is to restrict the returned type here to only Pick read methods
     * from the Graph class, but thast a huge amount of methods...
     * Tracked by ticket: https://github.com/buildwithflux/flux-app/issues/8955
     */
    getGraph() {
        if (!this.isDataUpdated)
            return undefined;
        return this.graph;
    }
    /**
     * It is recommended to call this method to retrieve a graph node because it first checks
     * existense for you. Otherwise, calling `getNodeAttributes` directly could throw error if
     * node doesn't exist
     */
    getNodeAttributes(nodeId) {
        if (!this.isDataUpdated)
            return null;
        if (!this.graph.hasNode(nodeId))
            return null;
        return this.graph.getNodeAttributes(nodeId);
    }
    getConnectedComponents() {
        if (!this.isDataUpdated)
            return undefined;
        return this.connectedComponents;
    }
    getNetToConnectedComponents() {
        if (!this.isDataUpdated)
            return undefined;
        return this.netToConnectedComponents;
    }
    subscribe(callback) {
        return this.subscriptionManager.addSubscription("update", callback);
    }
    clear() {
        this.graph.clear();
        this.connectedComponents = [];
        this.netToConnectedComponents = {};
        this.isDataUpdated = false;
    }
    invalidateGraph() {
        this.isDataUpdated = false;
    }
    /**
     * On every update of the graph, we construct the RBush by feeding in all
     * existing graph nodes + new graph nodes.
     *
     * We do so instead of incrementally update it because bulk insert is more performant
     * according to their doc: https://github.com/mourner/rbush#bulk-inserting-data
     */
    constructRTree() {
        const tree = new RBush();
        const allGraphNodes = [];
        this.graph.forEachNode((_node, attributes) => allGraphNodes.push(attributes));
        tree.load(allGraphNodes);
        return tree;
    }
    /**
     * Remove any nodes/edges from graph. Note that `dropNode` also drops any edges
     * connected to the dropped node
     */
    removeNodes(removeNodeUids, onNeighborOfRemovedNode) {
        for (const removedNodeUid of removeNodeUids) {
            // Handle Pad/Via removal
            if (this.graph.hasNode(removedNodeUid)) {
                // eslint-disable-next-line @buildwithflux/no-array-foreach
                this.graph.neighbors(removedNodeUid).forEach(onNeighborOfRemovedNode);
                this.graph.dropNode(removedNodeUid);
            }
            // Handle RouteSegment removal. Removing nodes will also remove the connected edges
            const routeStartNodeUid = `${removedNodeUid}_start`;
            const routeEndNodeUid = `${removedNodeUid}_end`;
            if (this.graph.hasNode(routeStartNodeUid)) {
                // eslint-disable-next-line @buildwithflux/no-array-foreach
                this.graph.neighbors(routeStartNodeUid).forEach(onNeighborOfRemovedNode);
                this.graph.dropNode(routeStartNodeUid);
            }
            if (this.graph.hasNode(routeEndNodeUid)) {
                // eslint-disable-next-line @buildwithflux/no-array-foreach
                this.graph.neighbors(routeEndNodeUid).forEach(onNeighborOfRemovedNode);
                this.graph.dropNode(routeEndNodeUid);
            }
        }
    }
    // For each graph node, we try to find the connected stuff by using the distance between them (including layers)
    // For example traces that ends directly on top of pads or two trace extremes
    updateConnectivityByDistance(newNodes, tree) {
        const searchBB = { minX: 0, minY: 0, maxX: 0, maxY: 0 };
        for (const node of newNodes) {
            searchBB.minX = node.minX - traceGraphTolerance;
            searchBB.minY = node.minY - traceGraphTolerance;
            searchBB.maxX = node.maxX + traceGraphTolerance;
            searchBB.maxY = node.maxY + traceGraphTolerance;
            // Loop through all neighbors connected to the node, remove any invalid "foundEdge". This should
            // be a negate of the following blog of code which adds "foundEdge" to the graph
            this.graph.forEachNeighbor(node.uid, (neighborKey, neighborAttributes) => {
                // Edge must exist at this point so no need to check existence
                const edge = this.graph.getEdgeAttributes(node.uid, neighborKey);
                // We only care about "foundEdge" here.
                if (edge.type !== "foundEdge")
                    return;
                // If not in the same layer, drop the edge
                if (!areNodesInSameLayer(node, neighborAttributes)) {
                    this.graph.dropEdge(node.uid, neighborKey);
                }
                if (!areNodesConnected(neighborAttributes, node)) {
                    this.graph.dropEdge(node.uid, neighborKey);
                }
            });
            // Add "foundEdge" to connect nodes in graph, based on layer and distance
            const nearbyNodes = tree.search(searchBB);
            for (const node2 of nearbyNodes) {
                const differentNode = node.uid !== node2.uid;
                if (!differentNode)
                    continue;
                if (!areNodesInSameLayer(node, node2))
                    continue;
                const shouldAdd = areNodesConnected(node2, node);
                const startUid = node.uid;
                const endUid = node2.uid;
                if (shouldAdd && !this.graph.hasEdge(startUid, endUid)) {
                    this.graph.addEdge(startUid, endUid, { type: "foundEdge", startUid, endUid });
                }
            }
        }
    }
    /**
     * Helper function to set graph nodes/edges to the graph
     */
    setGraphObjects(newNodes, newEdges) {
        for (const node of newNodes) {
            // NOTE: `mergeNode` handles both add/update cases
            // "Adds a node only if the node does not exist in the graph yet. Else it will merge the provided attributes with the already existing ones."
            this.graph.mergeNode(node.uid, node);
        }
        for (const edge of newEdges) {
            if (!this.graph.hasEdge(edge.startUid, edge.endUid)) {
                this.graph.addEdge(edge.startUid, edge.endUid, edge);
            }
        }
    }
    /**
     * Given the DRC input, generate all graph objects we will add to PcbTracingGraph, they
     * include both GraphNodes and GraphEdges
     */
    generateGraphObjects(inputs, updatedNodeUids) {
        // First generate all the graph nodes/edges from inputs
        const viaGraphNodes = this.generateViaGraphNodes(inputs.pcbLayoutNodes, updatedNodeUids);
        const padGraphNodes = this.generatePadGraphNodes(inputs.pcbLayoutNodes, updatedNodeUids);
        const routeGraphNodesAndEdges = this.generateRouteSegmentGraphNodesAndEdges(inputs.pcbLayoutNodes, updatedNodeUids);
        const routeGraphNodes = routeGraphNodesAndEdges.filter((object) => object.type === "routeNode");
        const edges = routeGraphNodesAndEdges.filter((object) => object.type === "routeEdge");
        const newGraphNodes = [...padGraphNodes, ...routeGraphNodes, ...viaGraphNodes];
        return {
            newNodes: newGraphNodes,
            newEdges: edges,
        };
    }
    generateRouteSegmentGraphNodesAndEdges(pcbNodes, updatedNodeUids) {
        // RouteSegment nodes
        // Let's collect info about our traces: every trace is two graph nodes and an arc
        let routeGraphNodesAndEdges = [];
        const updatedRouteSegmentNodes = updatedNodeUids
            .map((uid) => pcbNodes[uid])
            .filter((node) => node?.type === PcbNodeTypes.routeSegment);
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        updatedRouteSegmentNodes.forEach((node) => {
            const rs = node;
            const absolutePos = vector3FromXYZ(rs.bakedRules.positionRelativeToDocument ?? ZeroVector3); // TODO: fix - this should not be nessisary! this was done to avoid CI failure caused by "natarius-cool-demo-project.flx" in PcbLayoutEngine.test
            const absoluteRot = eulerFromXYZ(rs.bakedRules.rotationRelativeToDocument ?? ZeroEuler);
            const layoutRelPos = vector3FromXYZ(rs.bakedRules?.positionRelativeToRootLayout ?? ZeroVector3);
            const layoutRelRot = eulerFromXYZ(rs.bakedRules?.rotationRelativeToRootLayout ?? ZeroEuler);
            const start = vector3FromXYZ(rs.bakedRules.startPosition);
            const end = vector3FromXYZ(rs.bakedRules.endPosition);
            const size = vector3FromXYZ(rs.bakedRules.size);
            const layer = rs.bakedRules.layer;
            const hasAllInfo = absolutePos && start && end && size && layoutRelPos && layoutRelRot;
            if (hasAllInfo) {
                const relativeDocumentEnd = end.clone().applyEuler(absoluteRot).add(absolutePos);
                const relativeDocumentStart = start.clone().applyEuler(absoluteRot).add(absolutePos);
                const relativeLayoutEnd = end.clone().applyEuler(layoutRelRot).add(layoutRelPos);
                const relativeLayoutStart = start.clone().applyEuler(layoutRelRot).add(layoutRelPos);
                const capRadius = size.x / 2;
                // Having NaN values breaks airwires completely
                // TODO: Revisit when rules parser has been fixed
                if (isNaN(relativeDocumentStart.x) ||
                    isNaN(relativeDocumentStart.y) ||
                    isNaN(relativeDocumentStart.z) ||
                    isNaN(capRadius)) {
                    return null;
                }
                routeGraphNodesAndEdges = routeGraphNodesAndEdges.concat([
                    {
                        type: "routeNode",
                        documentPos: vector2FromXY(relativeDocumentStart),
                        layoutPos: vector2FromXY(relativeLayoutStart),
                        uid: rs.uid + "_start",
                        nodeUid: rs.uid,
                        connectedLayers: [layer],
                        minX: relativeDocumentStart.x - capRadius,
                        minY: relativeDocumentStart.y - capRadius,
                        maxX: relativeDocumentStart.x + capRadius,
                        maxY: relativeDocumentStart.y + capRadius,
                    },
                    {
                        type: "routeNode",
                        documentPos: vector2FromXY(relativeDocumentEnd),
                        layoutPos: vector2FromXY(relativeLayoutEnd),
                        uid: rs.uid + "_end",
                        nodeUid: rs.uid,
                        connectedLayers: [layer],
                        minX: relativeDocumentEnd.x - capRadius,
                        minY: relativeDocumentEnd.y - capRadius,
                        maxX: relativeDocumentEnd.x + capRadius,
                        maxY: relativeDocumentEnd.y + capRadius,
                    },
                    {
                        type: "routeEdge",
                        uid: rs.uid,
                        startUid: rs.uid + "_start",
                        endUid: rs.uid + "_end",
                    },
                ]);
            }
        });
        return routeGraphNodesAndEdges;
    }
    generateViaGraphNodes(pcbNodes, updatedNodeUids) {
        // Via nodes - they are just a single node
        const viaGraphNodes = [];
        const updatedViaNodes = updatedNodeUids
            .map((uid) => pcbNodes[uid])
            .filter((node) => node?.type === PcbNodeTypes.via);
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        updatedViaNodes.forEach((node) => {
            const via = node;
            const absolutePos = vector3FromXYZ(via.bakedRules?.positionRelativeToDocument ?? ZeroVector3); // TODO: fix - this should not be nessisary! this was done to avoid CI failure caused by "natarius-cool-demo-project.flx" in PcbLayoutEngine.test
            const layoutRelPos = vector3FromXYZ(via.bakedRules?.positionRelativeToRootLayout ?? ZeroVector3);
            const size = vector3FromXYZ(via.bakedRules?.size);
            const connectedLayers = via.bakedRules.connectedLayers;
            if (absolutePos && size && connectedLayers) {
                const maxHalfSize = Math.max(size.x, size.y) / 2;
                const isSingleLayer = via.bakedRules.viaType === PcbViaType.microVia;
                const layer = via.bakedRules.layer;
                const connectedLayersNorm = isSingleLayer
                    ? [layer]
                    : connectedLayers[0] === "All"
                        ? "All"
                        : connectedLayers;
                // Having NaN values breaks airwires completely
                // TODO: Revisit when rules parser has been fixed
                if (!isNaN(absolutePos.x) && !isNaN(absolutePos.y) && !isNaN(absolutePos.z) && !isNaN(maxHalfSize)) {
                    viaGraphNodes.push({
                        type: "via",
                        uid: via.uid,
                        documentPos: vector2FromXY(absolutePos),
                        layoutPos: vector2FromXY(layoutRelPos),
                        connectedLayers: connectedLayersNorm,
                        minX: absolutePos.x - maxHalfSize / 2,
                        minY: absolutePos.y - maxHalfSize / 2,
                        maxX: absolutePos.x + maxHalfSize / 2,
                        maxY: absolutePos.y + maxHalfSize / 2,
                    });
                }
            }
        });
        return viaGraphNodes;
    }
    generatePadGraphNodes(pcbNodes, updatedNodeUids) {
        const updatedPadNodes = updatedNodeUids
            .map((uid) => pcbNodes[uid])
            .filter((node) => node?.type === PcbNodeTypes.pad);
        const padGraphNodes = [];
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        updatedPadNodes.forEach((node) => {
            const pad = node;
            const absolutePos = vector3FromXYZ(pad.bakedRules?.positionRelativeToDocument ?? ZeroVector3); // TODO: fix - this should not be nessisary! this was done to avoid CI failure caused by "natarius-cool-demo-project.flx" in PcbLayoutEngine.test
            const layoutRelPos = vector3FromXYZ(pad.bakedRules?.positionRelativeToRootLayout ?? ZeroVector3);
            const size = vector3FromXYZ(pad.bakedRules?.size);
            const connectedLayers = pad.bakedRules.connectedLayers;
            if (!absolutePos || !size || !connectedLayers) {
                // noop
            }
            else {
                const maxHalfSize = Math.max(size.x, size.y) / 2;
                const isSingleLayer = !pad.bakedRules.hole ||
                    pad.bakedRules.hole.holeType === FootPrintPadHoleType.SurfaceMountDevice ||
                    pad.bakedRules.hole.holeType === FootPrintPadHoleType.testPinOrCardEdgeConnector;
                const isPlatedTH = pad.bakedRules.hole && pad.bakedRules.hole.holeType === FootPrintPadHoleType.platedThroughHole;
                if (!isPlatedTH && !isSingleLayer) {
                    // noop
                }
                else {
                    const layer = pad.bakedRules.layer;
                    const connectedLayersNorm = isSingleLayer
                        ? [layer]
                        : connectedLayers[0] === "All"
                            ? "All"
                            : connectedLayers;
                    // Having NaN values breaks airwires completely
                    // TODO: Revisit when rules parser has been fixed
                    if (!isNaN(absolutePos.x) &&
                        !isNaN(absolutePos.y) &&
                        !isNaN(absolutePos.z) &&
                        !isNaN(maxHalfSize)) {
                        padGraphNodes.push({
                            type: "pad",
                            uid: pad.uid,
                            documentPos: vector2FromXY(absolutePos),
                            layoutPos: vector2FromXY(layoutRelPos),
                            connectedLayers: connectedLayersNorm,
                            minX: absolutePos.x - maxHalfSize / 2,
                            minY: absolutePos.y - maxHalfSize / 2,
                            maxX: absolutePos.x + maxHalfSize / 2,
                            maxY: absolutePos.y + maxHalfSize / 2,
                        });
                    }
                }
            }
        });
        return padGraphNodes;
    }
}
//# sourceMappingURL=PcbTracingGraph.js.map